import { memo, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Checkbox,
} from "@mui/material";
import KsbDialogActions from "../../../../2_ksbHub/components/KsbDialogActions";
import { Autocomplete } from "@mui/material";
import appService from "../../../../2_ksbHub/service/app.service";

function AddEventDialog({
  open,
  onClose,
  onSave,
  templates,
  selectedSchoolyear,
  initialData,
}) {
  const [label, setLabel] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [roles, setRoles] = useState({
    teacher: true,
    student: true,
  });

  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const isEditMode = !!initialData;
  const ROLE_TEACHER = 2;
  const ROLE_STUDENT = 3;

  useEffect(() => {
    const loadUsers = async () => {
      const data = await appService.getUsers();
      setAdminUsers(data);
    };
    loadUsers();
  }, []);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setLabel(initialData.label || "");
        setTemplateId(initialData.templateId ?? null);

        setRoles({
          teacher: initialData.roles?.includes(ROLE_TEACHER),
          student: initialData.roles?.includes(ROLE_STUDENT),
        });

        // ✅ IMPORTANT: set selected admins
        // assumes initialData.admins = array of user objects or IDs
        if (initialData.admins && adminUsers.length > 0) {
          const selected = adminUsers.filter((u) =>
            initialData.admins.includes(u.id),
          );
          setSelectedAdmins(selected);
        }
      } else {
        // ✅ reset when creating
        setLabel("");
        setTemplateId(null);
        setRoles({ teacher: true, student: true });
        setSelectedAdmins([]);
      }
    }
  }, [open, initialData, adminUsers]);

  // ✅ submit handler
  const handleSubmit = () => {
    if (!label.trim() || !selectedSchoolyear?.id) return;

    const roleIds = [];
    if (roles.teacher) roleIds.push(2);
    if (roles.student) roleIds.push(3);

    onSave({
      id: initialData?.id, // ✅ needed for update
      eventLabel: label.trim(),
      templateId,
      roles: roleIds,
      admins: selectedAdmins.map((u) => u.id),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {isEditMode ? "Event bearbeiten" : "Neuen Event erstellen"}

      <DialogContent>
        {/* ✅ LABEL INPUT */}
        <TextField
          fullWidth
          label="Eventname (z.B. Sommeranlass)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ mt: 2 }}
        />

        <Autocomplete
          multiple
          options={adminUsers}
          getOptionLabel={(u) =>
            `${u.email}${u.abbreviation ? ` (${u.abbreviation})` : ""}`
          }
          value={selectedAdmins}
          filterSelectedOptions
          onChange={(event, newValue) => setSelectedAdmins(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Event Admins" sx={{ mt: 2 }} />
          )}
        />

        {/* ✅ TEMPLATE SECTION */}
        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Vorlage auswählen
        </Typography>

        {/* ✅ RADIO GROUP */}
        {templates && templates.length > 0 ? (
          <RadioGroup
            value={templateId ?? ""}
            onChange={(e) => {
              const value = e.target.value;

              // ✅ IMPORTANT FIX → prevent NaN
              setTemplateId(value ? Number(value) : null);
            }}
            sx={{ mt: 1 }}
          >
            {/* ✅ BLANK OPTION */}
            <FormControlLabel
              value=""
              control={<Radio />}
              label="Leere Vorlage"
            />

            {/* ✅ TEMPLATE OPTIONS */}
            {templates.map((t) => (
              <FormControlLabel
                key={t.id}
                value={t.id}
                control={<Radio />}
                label={t.name}
              />
            ))}
          </RadioGroup>
        ) : (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Keine Vorlagen verfügbar
          </Typography>
        )}
        {/* ✅ ROLE VISIBILITY */}
        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Sichtbarkeit
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={roles.teacher}
              onChange={(e) =>
                setRoles({ ...roles, teacher: e.target.checked })
              }
            />
          }
          label="Lehrpersonen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={roles.student}
              onChange={(e) =>
                setRoles({ ...roles, student: e.target.checked })
              }
            />
          }
          label="Lernende"
        />
      </DialogContent>

      <KsbDialogActions
        onCancel={onClose}
        onConfirm={handleSubmit}
        confirmLabel={isEditMode ? "Speichern" : "Erstellen"}
        confirmDisabled={!label.trim()}
      />
    </Dialog>
  );
}

export default memo(AddEventDialog);
